import React, { useState } from 'react';
import { FileTextIcon, TrendingUpIcon, AlertCircleIcon, ClipboardCheckIcon, CheckCircleIcon, ClockIcon, CalendarIcon, TruckIcon, BarChartIcon, PlusIcon, FilterIcon, ShoppingCartIcon, XIcon, CheckIcon, TagIcon, DollarSignIcon, PackageIcon } from 'lucide-react';
import { formatCurrency } from '../utils/currencyFormatter';
export function ProcurementDashboard({
  requisitions,
  purchaseOrders,
  setCurrentView,
  locale = 'en-US'
}) {
  // Calculate summary statistics
  const totalRequisitions = requisitions.length;
  const pendingApproval = requisitions.filter(req => req.status === 'Pending Approval').length;
  const totalReqSpend = requisitions.reduce((sum, req) => sum + req.total, 0);
  // PO statistics
  const totalPurchaseOrders = purchaseOrders.length;
  const pendingPOs = purchaseOrders.filter(po => po.status === 'Pending Confirmation' || po.status === 'Draft').length;
  const totalPOSpend = purchaseOrders.reduce((sum, po) => sum + po.total, 0);
  // Get recent requisitions (last 3)
  const recentRequisitions = [...requisitions].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 3);
  // Get recent POs (last 3)
  const recentPurchaseOrders = [...purchaseOrders].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 3);
  // Sample data for procurement insights
  const pendingTasks = [{
    id: 'TASK-001',
    type: 'Requisition',
    name: 'Office Supplies Approval',
    dueDate: '2023-11-20',
    priority: 'High',
    status: 'Pending Approval'
  }, {
    id: 'TASK-002',
    type: 'Purchase Order',
    name: 'Server Hardware Confirmation',
    dueDate: '2023-11-18',
    priority: 'Medium',
    status: 'Pending Confirmation'
  }, {
    id: 'TASK-003',
    type: 'Receipt',
    name: 'IT Equipment Delivery',
    dueDate: '2023-11-25',
    priority: 'Low',
    status: 'Expected'
  }];
  const upcomingDeliveries = [{
    id: 'PO-2023-001',
    vendor: 'Server Solutions Inc.',
    items: 'Server Hardware',
    expectedDate: '2023-11-22',
    status: 'In Transit',
    trackingNumber: 'TRK123456789'
  }, {
    id: 'PO-2023-002',
    vendor: 'PrintWorks Inc.',
    items: 'Marketing Materials',
    expectedDate: '2023-11-25',
    status: 'Processing',
    trackingNumber: null
  }];
  const topSpendCategories = [{
    category: 'IT Equipment',
    spend: 12500,
    percentage: 40
  }, {
    category: 'Office Supplies',
    spend: 8000,
    percentage: 25
  }, {
    category: 'Marketing',
    spend: 6200,
    percentage: 20
  }, {
    category: 'Services',
    spend: 4800,
    percentage: 15
  }];
  // Dashboard time filter state
  const [timeFilter, setTimeFilter] = useState('30days');
  return <div data-id="element-13892">
      <div className="mb-8 flex justify-between items-center" data-id="element-13893">
        <div data-id="element-13894">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-13895">
            Procurement Dashboard
          </h2>
          <p className="mt-1 text-sm text-gray-500" data-id="element-13896">
            Overview of requisitions, purchase orders, and goods receipts
          </p>
        </div>
        <div className="flex items-center space-x-3" data-id="element-13897">
          <div className="flex items-center" data-id="element-13898">
            <FilterIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-13899" />
            <select value={timeFilter} onChange={e => setTimeFilter(e.target.value)} className="block text-sm border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500" data-id="element-13900">
              <option value="7days" data-id="element-13901">Last 7 days</option>
              <option value="30days" data-id="element-13902">Last 30 days</option>
              <option value="90days" data-id="element-13903">Last 90 days</option>
              <option value="ytd" data-id="element-13904">Year to date</option>
            </select>
          </div>
          <button onClick={() => setCurrentView('create-requisition')} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-13905">
            <PlusIcon className="h-4 w-4 mr-1" data-id="element-13906" />
            New Requisition
          </button>
        </div>
      </div>
      {/* Summary Cards Section */}
      <div className="mb-8" data-id="element-13907">
        <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-13908">
          Procurement Overview
        </h3>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-13909">
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => setCurrentView('requisitions')} data-id="element-13910">
            <div className="p-5" data-id="element-13911">
              <div className="flex items-center" data-id="element-13912">
                <div className="flex-shrink-0" data-id="element-13913">
                  <FileTextIcon className="h-6 w-6 text-indigo-400" data-id="element-13914" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-13915">
                  <dl data-id="element-13916">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-13917">
                      Active Requisitions
                    </dt>
                    <dd data-id="element-13918">
                      <div className="text-lg font-medium text-gray-900" data-id="element-13919">
                        {totalRequisitions}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => setCurrentView('purchase-orders')} data-id="element-13920">
            <div className="p-5" data-id="element-13921">
              <div className="flex items-center" data-id="element-13922">
                <div className="flex-shrink-0" data-id="element-13923">
                  <ClipboardCheckIcon className="h-6 w-6 text-blue-400" data-id="element-13924" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-13925">
                  <dl data-id="element-13926">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-13927">
                      Active Purchase Orders
                    </dt>
                    <dd data-id="element-13928">
                      <div className="text-lg font-medium text-gray-900" data-id="element-13929">
                        {totalPurchaseOrders}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => setCurrentView('requisitions')} data-id="element-13930">
            <div className="p-5" data-id="element-13931">
              <div className="flex items-center" data-id="element-13932">
                <div className="flex-shrink-0" data-id="element-13933">
                  <AlertCircleIcon className="h-6 w-6 text-yellow-400" data-id="element-13934" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-13935">
                  <dl data-id="element-13936">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-13937">
                      Pending Approvals
                    </dt>
                    <dd data-id="element-13938">
                      <div className="text-lg font-medium text-gray-900" data-id="element-13939">
                        {pendingApproval + pendingPOs}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" data-id="element-13940">
            <div className="p-5" data-id="element-13941">
              <div className="flex items-center" data-id="element-13942">
                <div className="flex-shrink-0" data-id="element-13943">
                  <DollarSignIcon className="h-6 w-6 text-green-400" data-id="element-13944" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-13945">
                  <dl data-id="element-13946">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-13947">
                      Total Procurement Spend
                    </dt>
                    <dd data-id="element-13948">
                      <div className="text-lg font-medium text-gray-900" data-id="element-13949">
                        {formatCurrency(totalPOSpend, 'USD', locale)}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Procurement Metrics */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 mb-8" data-id="element-13950">
        {/* Pending Tasks */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-13951">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-13952">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-13953">
              Pending Tasks
            </h3>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-13954">
              {pendingTasks.length} items
            </span>
          </div>
          <ul className="divide-y divide-gray-200" data-id="element-13955">
            {pendingTasks.map(task => <li key={task.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50 cursor-pointer" onClick={() => {
            if (task.type === 'Requisition') {
              setCurrentView('requisitions');
            } else if (task.type === 'Purchase Order') {
              setCurrentView('purchase-orders');
            } else {
              setCurrentView('receipting');
            }
          }} data-id="element-13956">
                <div className="flex items-center justify-between" data-id="element-13957">
                  <div className="flex items-center" data-id="element-13958">
                    <div className="flex-shrink-0" data-id="element-13959">
                      {task.type === 'Requisition' ? <FileTextIcon className="h-5 w-5 text-indigo-400" data-id="element-13960" /> : task.type === 'Purchase Order' ? <ClipboardCheckIcon className="h-5 w-5 text-blue-400" data-id="element-13961" /> : <TruckIcon className="h-5 w-5 text-green-400" data-id="element-13962" />}
                    </div>
                    <div className="ml-3" data-id="element-13963">
                      <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-13964">
                        {task.name}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-13965">
                        {task.id} • {task.type}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center" data-id="element-13966">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${task.priority === 'High' ? 'bg-red-100 text-red-800' : task.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`} data-id="element-13967">
                      {task.priority}
                    </span>
                    <span className="text-sm text-gray-500 ml-4" data-id="element-13968">
                      Due: {task.dueDate}
                    </span>
                  </div>
                </div>
              </li>)}
          </ul>
        </div>
        {/* Upcoming Deliveries */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-13969">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-13970">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-13971">
              Upcoming Deliveries
            </h3>
            <button onClick={() => setCurrentView('receipting')} className="text-sm text-indigo-600 hover:text-indigo-900" data-id="element-13972">
              View all
            </button>
          </div>
          <ul className="divide-y divide-gray-200" data-id="element-13973">
            {upcomingDeliveries.map(delivery => <li key={delivery.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50 cursor-pointer" onClick={() => setCurrentView('receipting')} data-id="element-13974">
                <div className="flex items-center justify-between" data-id="element-13975">
                  <div className="flex items-center" data-id="element-13976">
                    <div className="flex-shrink-0" data-id="element-13977">
                      <TruckIcon className="h-5 w-5 text-blue-400" data-id="element-13978" />
                    </div>
                    <div className="ml-3" data-id="element-13979">
                      <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-13980">
                        {delivery.items}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-13981">
                        {delivery.id} • {delivery.vendor}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center" data-id="element-13982">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${delivery.status === 'In Transit' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-13983">
                      {delivery.status}
                    </span>
                    <span className="text-sm text-gray-500 ml-4" data-id="element-13984">
                      Expected: {delivery.expectedDate}
                    </span>
                  </div>
                </div>
                {delivery.trackingNumber && <div className="mt-2 text-xs text-gray-500" data-id="element-13985">
                    Tracking: {delivery.trackingNumber}
                  </div>}
              </li>)}
          </ul>
        </div>
      </div>
      {/* Spend by Category */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-13986">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6" data-id="element-13987">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-13988">
            Spend by Category
          </h3>
        </div>
        <div className="px-4 py-5 sm:p-6" data-id="element-13989">
          <div className="space-y-4" data-id="element-13990">
            {topSpendCategories.map(category => <div key={category.category} data-id="element-13991">
                <div className="flex items-center justify-between mb-1" data-id="element-13992">
                  <div className="flex items-center" data-id="element-13993">
                    <TagIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-13994" />
                    <span className="text-sm font-medium text-gray-700" data-id="element-13995">
                      {category.category}
                    </span>
                  </div>
                  <span className="text-sm font-medium text-gray-900" data-id="element-13996">
                    {formatCurrency(category.spend, 'USD', locale)} (
                    {category.percentage}%)
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-13997">
                  <div className="bg-indigo-600 h-2.5 rounded-full" style={{
                width: `${category.percentage}%`
              }} data-id="element-13998"></div>
                </div>
              </div>)}
          </div>
        </div>
      </div>
      {/* Recent Requisitions and Purchase Orders */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 mb-8" data-id="element-13999">
        <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-14000">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-14001">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-14002">
              Recent Requisitions
            </h3>
            <button onClick={() => setCurrentView('requisitions')} className="text-sm text-indigo-600 hover:text-indigo-900" data-id="element-14003">
              View all
            </button>
          </div>
          <ul className="divide-y divide-gray-200" data-id="element-14004">
            {recentRequisitions.map(req => <li key={req.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50 cursor-pointer" onClick={() => setCurrentView('requisitions')} data-id="element-14005">
                <div className="flex items-center justify-between" data-id="element-14006">
                  <div className="flex items-center" data-id="element-14007">
                    <div className="flex-shrink-0" data-id="element-14008">
                      <FileTextIcon className="h-5 w-5 text-indigo-400" data-id="element-14009" />
                    </div>
                    <div className="ml-3" data-id="element-14010">
                      <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-14011">
                        {req.name}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-14012">
                        {req.id} • {req.vendor}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center" data-id="element-14013">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${req.status === 'Approved' ? 'bg-green-100 text-green-800' : req.status === 'Draft' ? 'bg-gray-100 text-gray-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-14014">
                      {req.status}
                    </span>
                    <span className="text-sm text-gray-500 ml-4" data-id="element-14015">
                      {formatCurrency(req.total, 'USD', locale)}
                    </span>
                  </div>
                </div>
              </li>)}
          </ul>
        </div>
        <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-14016">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-14017">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-14018">
              Recent Purchase Orders
            </h3>
            <button onClick={() => setCurrentView('purchase-orders')} className="text-sm text-indigo-600 hover:text-indigo-900" data-id="element-14019">
              View all
            </button>
          </div>
          <ul className="divide-y divide-gray-200" data-id="element-14020">
            {recentPurchaseOrders.map(po => <li key={po.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50 cursor-pointer" onClick={() => setCurrentView('purchase-orders')} data-id="element-14021">
                <div className="flex items-center justify-between" data-id="element-14022">
                  <div className="flex items-center" data-id="element-14023">
                    <div className="flex-shrink-0" data-id="element-14024">
                      <ClipboardCheckIcon className="h-5 w-5 text-blue-400" data-id="element-14025" />
                    </div>
                    <div className="ml-3" data-id="element-14026">
                      <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-14027">
                        {po.name}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-14028">
                        {po.id} • {po.vendor}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center" data-id="element-14029">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${po.status === 'Sent to Vendor' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-14030">
                      {po.status}
                    </span>
                    <span className="text-sm text-gray-500 ml-4" data-id="element-14031">
                      {formatCurrency(po.total, 'USD', locale)}
                    </span>
                  </div>
                </div>
              </li>)}
          </ul>
        </div>
      </div>
      {/* Procurement KPIs */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-14032">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6" data-id="element-14033">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-14034">
            Procurement KPIs
          </h3>
        </div>
        <div className="px-4 py-5 sm:p-6" data-id="element-14035">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-14036">
            <div className="bg-green-50 p-4 rounded-lg" data-id="element-14037">
              <div className="flex items-center" data-id="element-14038">
                <div className="flex-shrink-0 bg-green-100 rounded-md p-2" data-id="element-14039">
                  <CheckCircleIcon className="h-6 w-6 text-green-600" data-id="element-14040" />
                </div>
                <div className="ml-4" data-id="element-14041">
                  <h4 className="text-sm font-medium text-green-900" data-id="element-14042">
                    On-Time Delivery
                  </h4>
                  <p className="text-3xl font-semibold text-green-700" data-id="element-14043">92%</p>
                </div>
              </div>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg" data-id="element-14044">
              <div className="flex items-center" data-id="element-14045">
                <div className="flex-shrink-0 bg-blue-100 rounded-md p-2" data-id="element-14046">
                  <ClockIcon className="h-6 w-6 text-blue-600" data-id="element-14047" />
                </div>
                <div className="ml-4" data-id="element-14048">
                  <h4 className="text-sm font-medium text-blue-900" data-id="element-14049">
                    Avg. Processing Time
                  </h4>
                  <p className="text-3xl font-semibold text-blue-700" data-id="element-14050">
                    3.2 days
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-indigo-50 p-4 rounded-lg" data-id="element-14051">
              <div className="flex items-center" data-id="element-14052">
                <div className="flex-shrink-0 bg-indigo-100 rounded-md p-2" data-id="element-14053">
                  <PackageIcon className="h-6 w-6 text-indigo-600" data-id="element-14054" />
                </div>
                <div className="ml-4" data-id="element-14055">
                  <h4 className="text-sm font-medium text-indigo-900" data-id="element-14056">
                    Order Accuracy
                  </h4>
                  <p className="text-3xl font-semibold text-indigo-700" data-id="element-14057">98%</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}